import { useCallback } from 'react';
import { useContainer } from 'src/features/ioc';
import { hasNavigationPathname, toLocation } from 'src/lib/navigation/path';
import type { NavigateFunction, NavigateOptions, NavigationTarget } from 'src/lib/navigation/types';
import { useResolvedPath } from 'src/lib/router/react';
import { useRouterNavigation } from 'src/lib/router/react/context';

/**
 * Guarded replacement for react-router's imperative navigation hook.
 *
 * Relative targets are resolved at the component's matched route level before
 * crossing the host-owned NavigationService boundary. A host without navigation
 * exposes no navigation affordance.
 */
export const useNavigate = (): NavigateFunction => {
    const container = useContainer();
    const contextNavigation = useRouterNavigation();
    const navigationService = contextNavigation ?? container.getOptional('NavigationService');
    const routeBase = useResolvedPath('.');

    const navigate = useCallback(
        (to: NavigationTarget | number, options?: NavigateOptions): void => {
            if (!navigationService) return;
            const target =
                typeof to === 'number'
                    ? to
                    : // The resolved "." captures the route-relative base without
                      // coupling the public hook back to the router implementation.
                      (({ hash, pathname, search }) => ({ hash, pathname, search }))(
                          toLocation(to, {
                              ...navigationService.location.value,
                              pathname:
                                  options?.relative === 'path' || !hasNavigationPathname(to)
                                      ? navigationService.location.value.pathname
                                      : routeBase.pathname,
                          }),
                      );
            void navigationService.navigate(target, options);
        },
        [navigationService, routeBase.pathname],
    );

    return navigate satisfies NavigateFunction;
};
